"use client";

import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { Button } from "../../../elements/button/button";
import { TextD2C } from "../../../elements/text-d2c/text";
import { textD2CSizes } from "../../../elements/text-d2c/text-d2c.constants";
import { cn } from "../../../lib/utils";
import type { MainNavigationColumn } from "../../main-navigation/main-navigation.types";
import { getNavigationTrackingAttributes } from "../../main-navigation/navigation-tracking";
import { footerClasses } from "../footer.constants";

export interface FooterColumnProps {
  column: MainNavigationColumn;
  /**
   * Link columns open and close on their headline below the desktop
   * breakpoint; addresses always show their text.
   */
  variant: "links" | "address";
}

/**
 * Column of the footer navigation with its headline, text and level 2
 * entries. Level 3 entries and the column teaser belong to the header only.
 */
export function FooterColumn(props: Readonly<FooterColumnProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const contentId = useId();
  const { column } = props;
  const isAddress = props.variant === "address";
  // A column without a headline has nothing to open it with, so its entries
  // stay visible on mobile.
  const isCollapsible = !isAddress && Boolean(column.headline);
  const TextTag = isAddress ? "address" : "p";

  return (
    <div className="flex flex-col">
      {column.headline ? (
        <TextD2C color="white" style="no-mb" tagName="h2" variant="h6">
          {isCollapsible ? (
            <>
              {/* The rows keep the design's spacing; the tap area reaches
                  halfway into the gaps around them. */}
              <Button
                aria-controls={contentId}
                aria-expanded={isOpen}
                className={cn(
                  footerClasses.focusRing,
                  "after:-inset-y-5 relative flex w-full items-center justify-between gap-4 whitespace-normal text-left font-bold after:absolute after:inset-x-0 lg:hidden",
                  textD2CSizes.h6
                )}
                mode="D2C"
                onClick={() => setIsOpen(!isOpen)}
                type="button"
                unstyled={true}
              >
                {column.headline}
                <ChevronDown
                  aria-hidden="true"
                  className={cn(
                    "-my-[3px] opacity-60 transition-transform duration-200 motion-reduce:transition-none",
                    isOpen && "rotate-180"
                  )}
                />
              </Button>
              <span className="hidden lg:block">{column.headline}</span>
            </>
          ) : (
            column.headline
          )}
        </TextD2C>
      ) : null}

      <div
        className={cn(
          "flex flex-col items-start",
          column.headline && (isCollapsible ? "pt-3" : "pt-2.5"),
          column.headline && "lg:pt-6",
          isCollapsible && !isOpen && "max-lg:hidden"
        )}
        id={contentId}
      >
        {column.text ? (
          <TextTag className={footerClasses.text}>{column.text}</TextTag>
        ) : null}
        {column.items.length > 0 ? (
          <ul className="flex flex-col">
            {column.items.map((item) => (
              <li key={item.id}>
                <Button
                  className={footerClasses.link}
                  {...getNavigationTrackingAttributes(item, 2)}
                  href={item.href}
                  mode="D2C"
                  target={item.target}
                  unstyled={true}
                >
                  {item.label}
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
        {column.cta ? (
          <div className="pt-4">
            <Button
              className={footerClasses.cta}
              href={column.cta.href}
              mode="D2C"
              target={column.cta.target}
              unstyled={true}
            >
              {column.cta.label}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
